import { Component, OnInit, OnDestroy } from '@angular/core'
import { Router, NavigationEnd, Event } from '@angular/router'
import { filter, Subscription } from 'rxjs'
import { HealthCheckService } from './services/health-check.service'
import { AuthenticationService } from './services/authentication.service'
import { ModernAnalyticsService } from './services/modern-analytics.service'
import { CampaignTrackingService } from './services/campaign-tracking.service'
import { PageViewService } from './services/page-view.service'
import { AffiliateService } from './services/affiliate.service'
import { GlobalService } from './services/global.service'
import { OmnisendService } from './services/omnisend.service'
import { Meta, Title } from '@angular/platform-browser'
import { ParkingWebMcpService } from './services/parking-webmcp.service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'parkupp'
    private routerSubscription: Subscription | null = null

    constructor(
        private healthCheckService: HealthCheckService,
        public authService: AuthenticationService,
        private titleService: Title,
        private metaTagService: Meta,
        private analyticsService: ModernAnalyticsService,
        private campaignTrackingService: CampaignTrackingService,
        private pageViewService: PageViewService,
        private affiliateService: AffiliateService,
        private globalService: GlobalService,
        // OmnisendService is injected here purely to instantiate it at app
        // startup so its constructor injects the env-aware brand snippet on
        // every page (previously hardcoded to prod brandID in index.html).
        private _omnisendService: OmnisendService,
        private parkingWebMcp: ParkingWebMcpService,
        private router: Router
    ) {
        const unsubscribe = this.authService.auth.onAuthStateChanged((user) => {
            unsubscribe()
            if (user == null) {
                this.authService.signInAnonymously()
            }
        })
    }

    ngOnInit() {
        const config = this.globalService.enterpriseConfig
        const siteName = config.pageTitle || config.name || 'ParkUpp'
        this.titleService.setTitle(`${siteName} - Find monthly parking online`)
        this.metaTagService.addTags([
            { name: 'description', content: `Find nearby parking spots for your car. Find monthly parking online with ${siteName}.` },
            { property: 'og:site_name', content: siteName },
            { property: 'og:type', content: 'website' },
        ])

        // Initialize campaign tracking
        this.campaignTrackingService.initTracking()
        this.affiliateService.trackLandingClick()

        // Floating help (WhatsApp) only on front-end routes — not admin (component not mounted)
        this.updateShowFloatingHelp(this.router.url)
        this.parkingWebMcp.syncRoute(this.router.url)
        this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.updateShowFloatingHelp(event.urlAfterRedirects)
            this.parkingWebMcp.syncRoute(event.urlAfterRedirects)
        })
    }

    ngOnDestroy() {
        this.parkingWebMcp.ngOnDestroy()
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe()
        }
    }

    /** Show floating help (WhatsApp) only outside /admin — enterprise gating remains inside the component. */
    showFloatingHelp = true

    private updateShowFloatingHelp(url: string): void {
        const path = url.split('?')[0].split('#')[0]
        this.showFloatingHelp = !path.startsWith('/admin')
    }
}
